// Phase 139 — Free Classroom Posters download page.
//
// Ungated public page at /posters. Lists the seven Ontario
// transferable-skills posters as a grid of CSS preview cards, each
// with its own "Download PDF" button. A prominent "Download all
// seven" CTA sits above the grid. Optional newsletter capture lives
// at the bottom — never gates a download.
import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Archive,
  ArrowRight,
  Download,
  Eye,
  FileDown,
  Loader2,
  Sparkles,
} from "lucide-react";
import axios from "axios";
import { Button } from "../components/ui/button";
import TrilliumIcon from "../components/TrilliumIcon";
import useDocumentMeta from "../lib/useDocumentMeta";

// Lazy — the react-pdf bundle should NOT ship to teachers who never
// tap "Preview". Only loaded when the lightbox actually opens.
const PosterPreviewLightbox = lazy(() =>
  import("../components/PosterPreviewLightbox"),
);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Phase 139.D — seasonal welcome-kit variants. Same nine PDFs across
// all terms; the README copy + zip filename + CTA label change so the
// kit matches the moment the teacher landed on the page. The `month`
// → term mapping below assumes the typical Ontario school calendar.
const TERMS = {
  september: {
    cta: "Grab everything for September — zip",
    filename: "AtomicEdu_Skills_Welcome_Kit_September.zip",
  },
  january: {
    cta: "Restart your January with a fresh wall — zip",
    filename: "AtomicEdu_Skills_Welcome_Kit_January.zip",
  },
  spring: {
    cta: "Finish the year strong — zip",
    filename: "AtomicEdu_Skills_Welcome_Kit_Spring.zip",
  },
};
const TERM_KEYS = Object.keys(TERMS);

const defaultTermForMonth = (m /* 0-indexed */) => {
  // Jul–Oct → september. Nov–Feb → january. Mar–Jun → spring.
  if (m >= 6 && m <= 9) return "september";
  if (m >= 10 || m <= 1) return "january";
  return "spring";
};

const PosterCard = ({ poster, downloading, onDownload, onPreview }) => {
  const accent = poster.accent || "#1c2d3d";
  return (
    <article
      data-testid={`poster-card-${poster.slug}`}
      className="rounded-2xl border border-[#4a6984]/15 bg-white shadow-sm overflow-hidden flex flex-col"
    >
      {/* Real page-1 raster of the actual poster PDF — generated
          server-side via PyMuPDF from the same cached PDF the teacher
          downloads, so the preview never drifts from the file.
          Clicking it opens the full preview lightbox. */}
      <button
        type="button"
        onClick={() => onPreview?.(poster)}
        data-testid={`poster-preview-${poster.slug}`}
        aria-label={`Preview the ${poster.name} poster`}
        className="relative aspect-[17/22] w-full bg-[#f9fafb] overflow-hidden border-b border-[#4a6984]/10 group focus:outline-none focus-visible:ring-2 focus-visible:ring-[#96ded1]"
      >
        <img
          src={`${API}/posters/preview/${encodeURIComponent(poster.slug)}.png`}
          alt={`Preview of the ${poster.name} poster`}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
      </button>

      {/* Title + "where it leads" sit BELOW the preview now (used to
          live inside the stylised mini-preview). Keeps the gallery
          card legible without writing on top of the real PDF
          thumbnail. */}
      <div className="px-4 pt-4 pb-2">
        <h3 className="font-extrabold text-base text-[#1c2d3d] tracking-tight leading-tight">
          {poster.name}
        </h3>
        {poster.where ? (
          <p className="mt-1 text-xs text-[#4a6984]/85 leading-snug line-clamp-2">
            <span
              className="font-extrabold uppercase tracking-[0.16em] text-[10px] mr-1"
              style={{ color: accent }}
            >
              Where it leads
            </span>
            {poster.where}
          </p>
        ) : null}
      </div>

      {/* Action row — Preview opens the real PDF in a lightbox,
          Download bypasses the preview for teachers who already know
          what they want. */}
      <div className="p-4 border-t border-[#4a6984]/10 bg-[#f4f5f7]/40 flex flex-col sm:flex-row gap-2 mt-auto">
        <Button
          type="button"
          onClick={() => onPreview?.(poster)}
          data-testid={`poster-preview-btn-${poster.slug}`}
          className="flex-1 h-10 rounded-md font-extrabold text-sm inline-flex items-center justify-center gap-2 border-2 border-[#1c2d3d] bg-white text-[#1c2d3d] hover:bg-[#f4f5f7]"
        >
          <Eye className="h-4 w-4" />
          Preview
        </Button>
        <Button
          type="button"
          onClick={() => onDownload(poster)}
          disabled={downloading}
          data-testid={`poster-download-${poster.slug}`}
          className="flex-1 h-10 rounded-md font-extrabold text-sm inline-flex items-center justify-center gap-2 bg-[#1c2d3d] text-white hover:bg-[#0f1e2c] disabled:opacity-60"
        >
          {downloading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Preparing&hellip;
            </>
          ) : (
            <>
              <Download className="h-4 w-4" />
              Download PDF
            </>
          )}
        </Button>
      </div>
    </article>
  );
};

const downloadPdf = async (url, filename) => {
  const { data } = await axios.get(url, { responseType: "blob" });
  const objectUrl = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
};

export default function PostersPage() {
  const location = useLocation();
  useDocumentMeta({
    title: "Free Transferable Skills Posters for Your Classroom Wall",
    description:
      "Print-ready PDFs for the seven Ontario transferable skills, with student-friendly “I can…” look-fors. Free for Ontario classrooms — print the full set or just the ones you need.",
    image: "https://atomicedu.ca/og/posters.png",
    url: "https://atomicedu.ca/posters",
    type: "website",
  });
  const [posters, setPosters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloadingSlug, setDownloadingSlug] = useState("");
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [downloadingCareer, setDownloadingCareer] = useState(false);
  const [downloadingFamily, setDownloadingFamily] = useState(false);
  const [downloadingKit, setDownloadingKit] = useState(false);
  // Phase 140.C — PDF preview lightbox. Holds the currently-open
  // poster so we can lazy-load the real 8.5×11 PDF and let the
  // teacher confirm the full-page layout before committing to a
  // download. `null` = closed.
  const [previewing, setPreviewing] = useState(null);

  // Pick the seasonal term: explicit URL ?term= wins (campaign links);
  // otherwise default by current month so a teacher arriving in
  // January sees the January copy without anyone touching the URL.
  const term = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    const requested = (params.get("term") || "").toLowerCase().trim();
    if (TERM_KEYS.includes(requested)) return requested;
    return defaultTermForMonth(new Date().getMonth());
  }, [location.search]);
  const termCopy = TERMS[term] || TERMS.september;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/posters/list`);
        if (cancelled) return;
        setPosters(data?.posters || []);
      } catch {
        if (!cancelled) setError("Couldn't load the posters list — please refresh.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSingle = async (poster) => {
    setDownloadingSlug(poster.slug);
    try {
      const safe = poster.name.replace(/&/g, "and").replace(/\s+/g, "_");
      await downloadPdf(
        `${API}/posters/single/${encodeURIComponent(poster.slug)}`,
        `AtomicEdu_Poster_${safe}.pdf`,
      );
    } catch {
      // axios surfaces an error to console; user just sees the button reset.
    } finally {
      setDownloadingSlug("");
    }
  };

  const handleAll = async () => {
    setDownloadingAll(true);
    try {
      await downloadPdf(`${API}/posters/all`, "AtomicEdu_Posters_All_Seven.pdf");
    } catch {
      // ditto
    } finally {
      setDownloadingAll(false);
    }
  };

  const handleWelcomeKit = async () => {
    setDownloadingKit(true);
    try {
      // Forward any utm_* params from the current URL so the backend
      // can attribute the download in /admin/posters-stats.
      const params = new URLSearchParams(location.search || "");
      const out = new URLSearchParams();
      out.set("term", term);
      ["utm_source", "utm_medium", "utm_campaign"].forEach((k) => {
        const v = params.get(k);
        if (v) out.set(k, v);
      });
      await downloadPdf(
        `${API}/posters/welcome-kit?${out.toString()}`,
        termCopy.filename,
      );
    } catch {
      // silent on the hero CTA — UI just resets
    } finally {
      setDownloadingKit(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f4f5f7] text-[#4a6984]">
      {/* Slim public header — mirrors /try */}
      <header className="bg-[#1c2d3d] text-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="posters-home-link">
            <TrilliumIcon className="h-7 w-7 text-[#96ded1]" />
            <span className="font-black text-lg tracking-tight">Atomic Edu</span>
          </Link>
          <Link
            to="/pricing"
            data-testid="posters-plans-link"
            className="text-sm font-bold text-white/85 hover:text-white"
          >
            Plans &rarr;
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 pt-10 pb-20">
        {/* Hero — colleague-to-colleague, value first */}
        <section className="max-w-3xl">
          <p
            data-testid="posters-eyebrow"
            className="font-extrabold uppercase tracking-[0.22em] text-xs text-[#4a6984]/75 flex items-center gap-1.5"
          >
            <Sparkles className="h-3.5 w-3.5" /> Free for Ontario classrooms
          </p>
          <h1
            data-testid="posters-headline"
            className="mt-4 font-black text-4xl sm:text-5xl text-[#4a6984] tracking-tighter leading-[1.05]"
          >
            Transferable Skills posters
            <br />
            <span className="text-[#1c2d3d]">for your classroom wall.</span>
          </h1>
          <p className="mt-4 text-[#4a6984]/85 font-medium text-base sm:text-lg leading-relaxed">
            Print-ready PDFs for the seven Ontario transferable skills —
            student-friendly look-fors written in &ldquo;I can&hellip;&rdquo;
            language. Print the full set or just the ones you need.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 flex-wrap">
            <Button
              type="button"
              onClick={handleWelcomeKit}
              disabled={downloadingKit || loading}
              data-testid="posters-download-kit"
              className="ae-cta hover:ae-cta h-12 px-6 rounded-md font-extrabold inline-flex items-center justify-center gap-2 whitespace-normal text-center"
            >
              {downloadingKit ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Preparing&hellip;
                </>
              ) : (
                <>
                  <Archive className="h-4 w-4" />
                  {termCopy.cta}
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </Button>
            <Button
              type="button"
              onClick={handleAll}
              disabled={downloadingAll || loading}
              data-testid="posters-download-all"
              className="h-12 px-5 rounded-md font-extrabold inline-flex items-center justify-center gap-2 border-2 border-[#1c2d3d] bg-white text-[#1c2d3d] hover:bg-[#f4f5f7] whitespace-normal text-center"
            >
              {downloadingAll ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Preparing&hellip;
                </>
              ) : (
                <>
                  <FileDown className="h-4 w-4" />
                  Just the 7 posters — PDF
                </>
              )}
            </Button>
            <Link
              to="/classroom-support"
              data-testid="posters-hub-link"
              className="inline-flex items-center justify-center gap-2 h-12 px-5 rounded-md font-extrabold text-sm border-2 border-[#4a6984]/25 bg-white text-[#1c2d3d] hover:border-[#1c2d3d] transition-colors whitespace-normal text-center"
            >
              Explore the Hub
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <p className="mt-3 text-xs text-[#4a6984]/65">
            The zip includes all 7 posters + Career Connection cards +
            Family Newsletter pack + a README. ~150KB.
          </p>
        </section>

        {/* Grid */}
        <section className="mt-14" aria-labelledby="posters-grid-heading">
          <h2
            id="posters-grid-heading"
            className="sr-only"
          >
            The seven posters
          </h2>
          {loading ? (
            <div
              data-testid="posters-loading"
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="aspect-[17/22] rounded-2xl bg-[#4a6984]/8 animate-pulse"
                />
              ))}
            </div>
          ) : error ? (
            <p data-testid="posters-error" className="text-red-600 font-medium">
              {error}
            </p>
          ) : (
            <div
              data-testid="posters-grid"
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {posters.map((p) => (
                <PosterCard
                  key={p.slug}
                  poster={p}
                  downloading={downloadingSlug === p.slug}
                  onDownload={handleSingle}
                  onPreview={setPreviewing}
                />
              ))}
            </div>
          )}
        </section>

        {/* Soft newsletter line — never gates a download */}
        <section
          data-testid="posters-print-pack"
          className="mt-16 rounded-2xl border border-[#4a6984]/15 bg-white px-6 py-7 sm:px-8 sm:py-8"
        >
          <p className="text-[10px] font-extrabold uppercase tracking-[0.22em] text-[#4a6984]/70">
            More free print resources
          </p>
          <h2 className="mt-1 font-black text-2xl sm:text-3xl text-[#1c2d3d] tracking-tight">
            Round out your Print Pack.
          </h2>
          <p className="mt-2 text-sm text-[#4a6984]/85 max-w-2xl">
            Two more lightweight printables that pair with the posters —
            same skills, family- and career-facing voice.
          </p>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
            <article
              data-testid="print-pack-career-cards"
              className="rounded-xl border border-[#4a6984]/15 bg-[#f4f5f7]/40 p-5 flex flex-col gap-3"
            >
              <div className="flex items-center gap-2 text-[#1c2d3d]">
                <div
                  className="h-2 w-10 rounded-full"
                  style={{ backgroundColor: "#3EB489" }}
                />
                <p className="text-[10px] font-extrabold uppercase tracking-[0.22em] text-[#4a6984]/80">
                  7 pages · one per skill
                </p>
              </div>
              <p className="font-extrabold text-lg text-[#1c2d3d] leading-snug">
                Career Connection cards
              </p>
              <p className="text-sm text-[#4a6984]/85 leading-relaxed flex-1">
                Three real Ontario jobs per skill, with a concrete &ldquo;skill
                in action&rdquo; example. Print for the hallway, the careers
                board, or parent-teacher interview night.
              </p>
              <Button
                type="button"
                onClick={async () => {
                  setDownloadingCareer(true);
                  try {
                    await downloadPdf(
                      `${API}/posters/career-cards`,
                      "AtomicEdu_Career_Connection_Cards.pdf",
                    );
                  } finally {
                    setDownloadingCareer(false);
                  }
                }}
                disabled={downloadingCareer}
                data-testid="print-pack-career-cards-download"
                className="h-10 px-4 rounded-md font-extrabold text-sm inline-flex items-center justify-center gap-2 bg-[#1c2d3d] text-white hover:bg-[#0f1e2c] disabled:opacity-60 w-full sm:w-auto"
              >
                {downloadingCareer ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Preparing&hellip;
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4" />
                    Download PDF
                  </>
                )}
              </Button>
            </article>

            <article
              data-testid="print-pack-family-blurb"
              className="rounded-xl border border-[#4a6984]/15 bg-[#f4f5f7]/40 p-5 flex flex-col gap-3"
            >
              <div className="flex items-center gap-2 text-[#1c2d3d]">
                <div
                  className="h-2 w-10 rounded-full"
                  style={{ backgroundColor: "#96ded1" }}
                />
                <p className="text-[10px] font-extrabold uppercase tracking-[0.22em] text-[#4a6984]/80">
                  1 page · paste-ready
                </p>
              </div>
              <p className="font-extrabold text-lg text-[#1c2d3d] leading-snug">
                Family Newsletter pack
              </p>
              <p className="text-sm text-[#4a6984]/85 leading-relaxed flex-1">
                Seven short paragraphs in plain language — paste into a
                term-end newsletter, parent-teacher interview prep, or
                your weekly class blog.
              </p>
              <Button
                type="button"
                onClick={async () => {
                  setDownloadingFamily(true);
                  try {
                    await downloadPdf(
                      `${API}/posters/family-blurb`,
                      "AtomicEdu_Family_Newsletter_Blurb.pdf",
                    );
                  } finally {
                    setDownloadingFamily(false);
                  }
                }}
                disabled={downloadingFamily}
                data-testid="print-pack-family-blurb-download"
                className="h-10 px-4 rounded-md font-extrabold text-sm inline-flex items-center justify-center gap-2 bg-[#1c2d3d] text-white hover:bg-[#0f1e2c] disabled:opacity-60 w-full sm:w-auto"
              >
                {downloadingFamily ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Preparing&hellip;
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4" />
                    Download PDF
                  </>
                )}
              </Button>
            </article>
          </div>
        </section>

        {/* Soft newsletter line — never gates a download */}
        <section
          data-testid="posters-newsletter"
          className="mt-12 rounded-xl border border-[#4a6984]/15 bg-white px-6 py-5 sm:px-8 sm:py-6"
        >
          <p className="text-[10px] font-extrabold uppercase tracking-[0.22em] text-[#4a6984]/70">
            Optional · skip if you like
          </p>
          <p className="mt-2 font-extrabold text-[#1c2d3d] text-lg leading-snug">
            Want more free classroom resources?
          </p>
          <p className="mt-1 text-sm text-[#4a6984]/85">
            We&apos;ll send the occasional new poster, planning tool, or
            free utility. No spam, no sales nonsense.
          </p>
          <Link
            to="/#newsletter"
            data-testid="posters-newsletter-link"
            className="mt-4 inline-flex items-center gap-2 text-sm font-extrabold text-[#1c2d3d] hover:underline"
          >
            Add your email &rarr;
          </Link>
        </section>

        <section
          className="mt-8 rounded-xl border border-[#4a6984]/15 bg-white px-6 py-5 sm:px-8 sm:py-6 border-l-4 border-l-[#3EB489]"
          data-testid="posters-reflection-cta"
        >
          <p className="text-[10px] font-extrabold uppercase tracking-[0.22em] text-[#4a6984]/70">
            New · year-end
          </p>
          <p className="mt-2 font-extrabold text-[#1c2d3d] text-lg leading-snug">
            Take 5 minutes to reflect on the year that was
          </p>
          <p className="mt-1 text-sm text-[#4a6984]/85 leading-relaxed">
            A tap-only year-end reflection across the seven Ontario
            transferable skills. We&apos;ll send your shape back to you
            when September shows up.
          </p>
          <Link
            to="/reflection"
            data-testid="posters-reflection-link"
            className="mt-4 inline-flex items-center gap-2 text-sm font-extrabold text-[#1c2d3d] hover:underline"
          >
            Open the reflection &rarr;
          </Link>
        </section>
      </main>

      {previewing && (
        <Suspense fallback={null}>
          <PosterPreviewLightbox
            poster={previewing}
            pdfUrl={`${API}/posters/single/${encodeURIComponent(previewing.slug)}`}
            downloading={downloadingSlug === previewing.slug}
            onClose={() => setPreviewing(null)}
            onDownload={() => handleSingle(previewing)}
          />
        </Suspense>
      )}
    </div>
  );
}
